import { useEffect, useState } from 'react'

import { Input, Popup } from '@skytech/ui'
import { Button } from '@skytech/ui/Button'
import { Container } from '@skytech/ui/Container'
import { useDebounce } from '@skytech/utils'
import { useQuery } from '@tanstack/react-query'
import PlusIcon from 'mdi-react/PlusIcon'
import { useNavigate } from 'react-router-dom'

import { Translation } from '@/app/translations/Translation'
import { useTranslation } from '@/app/translations/translations-hooks'
import { CustomerSelect } from '@/components/CustomerSelect'
import { useSearchParamsByKey } from '@/hooks/searchParams'
import adminResources from '@/modules/admin/resources'
import { APP_ROUTES } from '@/util/app-routes'

import queries from '../queries'

import { AddressList } from './AddressList'
import { AddressModal } from './AddressModal'

export const AddressListPage = () => {
  const navigate = useNavigate()
  const translate = useTranslation()
  const [addressModal, setAddressModal] = useState<{ isOpen: boolean; addressId?: number }>({
    isOpen: false,
  })
  const [querySearch, setQuerySearch] = useSearchParamsByKey('search')
  const [searchFilter, setSearchFilter] = useState(querySearch || '')
  const debouncedSearchTerm = useDebounce(searchFilter, 500)

  useEffect(() => {
    setSearchFilter(querySearch || '')
  }, [querySearch])

  useEffect(() => {
    setQuerySearch(debouncedSearchTerm)
  }, [debouncedSearchTerm, setQuerySearch])

  const {
    data: addressList,
    isLoading,
    isSuccess,
  } = useQuery(queries.adminAddresses(), adminResources.getAddressList)

  const addressDetails =
    addressList && addressModal.addressId
      ? addressList.find(({ id }) => id === addressModal.addressId)
      : null

  return (
    <Container onSwipedLeft={() => navigate(APP_ROUTES.HOME)}>
      <Container.Header>
        <Translation id="Addresses" />
      </Container.Header>

      <div className="mt-4 w-full">
        <Button
          Icon={<PlusIcon />}
          disabled={!isSuccess}
          className="mb-4 ml-0"
          onClick={() => setAddressModal({ isOpen: true })}
        >
          <Translation id="New address" />
        </Button>
        <div className="flex flex-col gap-4 md:flex-row md:items-center">
          <Input
            id="searchFilter"
            type="text"
            value={searchFilter}
            placeholder={translate('Search')}
            onChange={e => setSearchFilter(e.target.value)}
          />
          <CustomerSelect
            hasGlobalFilter
            filterData={addressList?.filter(item => item.customerId && item.customerName) || []}
            isLoading={isLoading}
          />
        </div>
      </div>

      <AddressList searchFilter={searchFilter} onClickAddressRow={setAddressModal} />

      <Popup
        variant="narrow"
        ariaLabel={translate('Add or edit address')}
        isOpen={addressModal.isOpen}
        onClose={() => setAddressModal({ isOpen: false })}
      >
        <AddressModal
          addressDetails={addressDetails || null}
          addressList={addressList || []}
          onClose={() => setAddressModal({ isOpen: false })}
        />
      </Popup>
    </Container>
  )
}
